import {
  Box,
  Flex,
  HStack,
  Link,
  IconButton,
  useDisclosure,
  useColorMode,
  useColorModeValue,
  Stack,
  Text,
  Button,
  Image,
  Icon,
} from "@chakra-ui/react";
import { HamburgerIcon, CloseIcon, MoonIcon, SunIcon } from "@chakra-ui/icons";
import { FiLock } from "react-icons/fi";
import { isTokenValid } from "utils/auth";
import { useHistory } from "react-router-dom";
import { Link as ScrollLink } from "react-scroll";
import { useState, useEffect } from "react";

function Header() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { colorMode, toggleColorMode } = useColorMode();
  const [scrolled, setScrolled] = useState(false);

  const isLoggedIn = isTokenValid();
  const history = useHistory();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const headerBg = (scrolled || isOpen)
    ? useColorModeValue(
      "linear-gradient(135deg, rgba(13, 148, 136, 0.98) 0%, rgba(6, 182, 212, 0.98) 100%)",
      "linear-gradient(135deg, rgba(11, 19, 41, 0.98) 0%, rgba(13, 148, 136, 0.9) 100%)"
    )
    : "transparent";

  const headerBorder = (scrolled || isOpen)
    ? useColorModeValue("rgba(13, 148, 136, 0.4)", "rgba(6, 182, 212, 0.25)")
    : "transparent";

  const headerFilter = (scrolled || isOpen) ? "blur(16px)" : "none";

  const navTextColor = (scrolled || isOpen)
    ? "white"
    : useColorModeValue("slate.700", "slate.100");

  const headerShadow = (scrolled || isOpen)
    ? "0 10px 30px rgba(0, 0, 0, 0.25)"
    : "none";

  return (
    <Box
      as="header"
      position="fixed"
      top="0"
      left="0"
      right="0"
      w="100%"
      bg={headerBg}
      backdropFilter={headerFilter}
      zIndex="1000"
      borderBottom="1px solid"
      borderColor={headerBorder}
      boxShadow={headerShadow}
      transition="all 0.35s cubic-bezier(0.4, 0, 0.2, 1)"
    >
      {/* Top Subtle Gradient Accent Line when transparent */}
      {!scrolled && !isOpen && (
        <Box h="3px" w="100%" bgGradient="linear(to-r, teal.400, cyan.400, blue.500)" />
      )}

      <Flex
        h={{ base: "68px", md: "74px" }}
        alignItems="center"
        justifyContent="space-between"
        maxW="1280px"
        mx="auto"
        px={{ base: 4, md: 8 }}
      >
        {/* Brand Logo & Title */}
        <Link
          href="/"
          to="/home"
          display="flex"
          alignItems="center"
          height="100%"
          py="6px"
          _hover={{ textDecoration: "none" }}
        >
          <HStack spacing={3} align="center">
            <Image
              src={scrolled || isOpen || colorMode === "dark" ? "/xpwhitelogo.png" : "/xplogo2.png"}
              alt="XP Tunisian Database Logo"
              maxH={{ base: "44px", md: "54px" }}
              objectFit="contain"
              transition="all 0.3s ease"
              _hover={{
                transform: "scale(1.05)",
                filter: "drop-shadow(0 0 12px rgba(13,148,136,0.5))",
              }}
            />
            <Box display="block">
              <Text
                fontWeight="900"
                fontSize={{ base: "xs", sm: "sm", md: "md" }}
                letterSpacing="0.04em"
                lineHeight="1.15"
                color={navTextColor}
                textTransform="uppercase"
              >
                Tunisian XP Database
              </Text>

            </Box>
          </HStack>
        </Link>

        {/* Desktop Navigation Links */}
        <HStack
          as="nav"
          spacing={2}
          display={{ base: "none", md: "flex" }}
          fontSize="sm"
          fontWeight="600"
        >
          <ScrollLink
            to="home"
            spy={true}
            smooth={true}
            duration={500}
            offset={-75}
            style={{ cursor: "pointer" }}
          >
            <Text
              as="span"
              display="inline-block"
              color={navTextColor}
              px="14px"
              py="8px"
              borderRadius="full"
              transition="all 0.25s cubic-bezier(0.4, 0, 0.2, 1)"
              _hover={{
                color: scrolled ? "cyan.200" : "teal.400",
                bg: scrolled ? "whiteAlpha.200" : (colorMode === "light" ? "rgba(13, 148, 136, 0.08)" : "rgba(6, 182, 212, 0.15)"),
                transform: "translateY(-1px)",
              }}
            >
              Home
            </Text>
          </ScrollLink>

          <ScrollLink
            to="features"
            spy={true}
            smooth={true}
            duration={500}
            offset={-75}
            style={{ cursor: "pointer" }}
          >
            <Text
              as="span"
              display="inline-block"
              color={navTextColor}
              px="14px"
              py="8px"
              borderRadius="full"
              transition="all 0.25s cubic-bezier(0.4, 0, 0.2, 1)"
              _hover={{
                color: scrolled ? "cyan.200" : "teal.400",
                bg: scrolled ? "whiteAlpha.200" : (colorMode === "light" ? "rgba(13, 148, 136, 0.08)" : "rgba(6, 182, 212, 0.15)"),
                transform: "translateY(-1px)",
              }}
            >
              Features
            </Text>
          </ScrollLink>

          <ScrollLink
            to="team"
            spy={true}
            smooth={true}
            duration={500}
            offset={-75}
            style={{ cursor: "pointer" }}
          >
            <Text
              as="span"
              display="inline-block"
              color={navTextColor}
              px="14px"
              py="8px"
              borderRadius="full"
              transition="all 0.25s cubic-bezier(0.4, 0, 0.2, 1)"
              _hover={{
                color: scrolled ? "cyan.200" : "teal.400",
                bg: scrolled ? "whiteAlpha.200" : (colorMode === "light" ? "rgba(13, 148, 136, 0.08)" : "rgba(6, 182, 212, 0.15)"),
                transform: "translateY(-1px)",
              }}
            >
              Team &amp; Sponsors
            </Text>
          </ScrollLink>

          <ScrollLink
            to="contact"
            spy={true}
            smooth={true}
            duration={500}
            offset={-75}
            style={{ cursor: "pointer" }}
          >
            <Text
              as="span"
              display="inline-block"
              color={navTextColor}
              px="14px"
              py="8px"
              borderRadius="full"
              transition="all 0.25s cubic-bezier(0.4, 0, 0.2, 1)"
              _hover={{
                color: scrolled ? "cyan.200" : "teal.400",
                bg: scrolled ? "whiteAlpha.200" : (colorMode === "light" ? "rgba(13, 148, 136, 0.08)" : "rgba(6, 182, 212, 0.15)"),
                transform: "translateY(-1px)",
              }}
            >
              Contact
            </Text>
          </ScrollLink>

          {/* Color Mode Toggle Button */}
          <IconButton
            size="sm"
            icon={colorMode === "light" ? <MoonIcon boxSize="14px" /> : <SunIcon boxSize="14px" />}
            aria-label="Toggle Theme"
            onClick={toggleColorMode}
            variant="ghost"
            borderRadius="full"
            color={navTextColor}
            mx={1}
            _hover={{
              bg: scrolled ? "whiteAlpha.200" : useColorModeValue("rgba(13, 148, 136, 0.12)", "whiteAlpha.200"),
              transform: "rotate(15deg)",
            }}
          />

          {/* Action Buttons */}
          {!isLoggedIn ? (
            <Button
              size="sm"
              onClick={() => history.push("/auth/signin")}
              bg={scrolled ? "white" : "teal.500"}
              color={scrolled ? "teal.800" : "white"}
              border="1px solid"
              borderColor={scrolled ? "white" : "teal.600"}
              borderRadius="full"
              px="20px"
              py="17px"
              fontWeight="700"
              fontSize="xs"
              leftIcon={<FiLock size="13" />}
              shadow={scrolled ? "0 4px 15px rgba(0,0,0,0.15)" : "0 4px 14px rgba(13, 148, 136, 0.35)"}
              _hover={{
                bg: scrolled ? "slate.100" : "teal.600",
                transform: "translateY(-1px)",
              }}
              transition="all 0.25s ease"
            >
              Member Access
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={() => history.push("/txd")}
              bg={scrolled ? "white" : undefined}
              bgGradient={!scrolled ? "linear(to-r, teal.400, cyan.500)" : undefined}
              color={scrolled ? "teal.700" : "white"}
              borderRadius="full"
              px="24px"
              py="18px"
              fontWeight="800"
              fontSize="sm"
              shadow={scrolled ? "0 4px 15px rgba(0,0,0,0.2)" : "0 4px 20px rgba(13, 148, 136, 0.4)"}
              _hover={{
                bg: scrolled ? "slate.100" : undefined,
                bgGradient: !scrolled ? "linear(to-r, teal.500, cyan.600)" : undefined,
                shadow: "0 8px 25px rgba(0,0,0,0.3)",
                transform: "translateY(-2px)",
              }}
            >
              Dashboard
            </Button>
          )}
        </HStack>

        {/* Mobile Navigation Controls */}
        <Flex display={{ md: "none" }} align="center">
          <IconButton
            size="sm"
            icon={colorMode === "light" ? <MoonIcon boxSize="14px" /> : <SunIcon boxSize="14px" />}
            aria-label="Toggle Theme"
            onClick={toggleColorMode}
            variant="ghost"
            borderRadius="full"
            color={navTextColor}
            mr="2"
          />
          <IconButton
            size="sm"
            icon={isOpen ? <CloseIcon boxSize="12px" /> : <HamburgerIcon boxSize="18px" />}
            aria-label="Toggle Navigation"
            onClick={isOpen ? onClose : onOpen}
            variant="ghost"
            borderRadius="full"
            color={navTextColor}
          />
        </Flex>
      </Flex>

      {/* Mobile Responsive Navigation Drawer */}
      {isOpen && (
        <Box
          pb={6}
          pt={3}
          display={{ md: "none" }}
          borderTop="1px solid"
          borderColor="whiteAlpha.200"
          bg="inherit"
          px={6}
        >
          <Stack as="nav" spacing={2}>
            {[
              { to: "home", label: "Home" },
              { to: "features", label: "Features" },
              { to: "team", label: "Team & Sponsors" },
              { to: "contact", label: "Contact" },
            ].map((link) => (
              <ScrollLink
                key={link.to}
                to={link.to}
                smooth={true}
                duration={500}
                offset={-75}
                onClick={onClose}
                style={{ cursor: "pointer" }}
              >
                <Text
                  color="white"
                  fontSize="sm"
                  fontWeight="700"
                  display="block"
                  py="10px"
                  px="14px"
                  borderRadius="12px"
                  transition="all 0.2s"
                  _hover={{ bg: "whiteAlpha.200", transform: "translateX(4px)" }}
                >
                  {link.label}
                </Text>
              </ScrollLink>
            ))}
            {!isLoggedIn ? (
              <Button
                size="sm"
                onClick={() => { onClose(); history.push("/auth/signin"); }}
                bg="white"
                color="teal.700"
                borderRadius="full"
                w="full"
                mt={3}
                py="20px"
                leftIcon={<FiLock size="14" />}
                fontSize="xs"
                fontWeight="800"
                shadow="0 4px 15px rgba(0,0,0,0.2)"
                _hover={{ bg: "slate.100" }}
              >
                Member Access
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={() => { onClose(); history.push("/txd"); }}
                bg="white"
                color="teal.700"
                borderRadius="full"
                w="full"
                mt={3}
                py="20px"
                fontSize="xs"
                fontWeight="800"
                shadow="0 4px 15px rgba(0,0,0,0.2)"
                _hover={{ bg: "slate.100" }}
              >
                Dashboard
              </Button>
            )}
          </Stack>
        </Box>
      )}
    </Box>
  );
}

export default Header;


